import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getGuardrailsViolation,
  acknowledgeViolation,
  resolveViolation,
  getGuardrailsResource,
} from '../../../services/cloudopsApi';
import { FiArrowLeft as ArrowLeft, FiCheckCircle as CheckCircle2, FiXCircle as XCircle } from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';

interface ViolationData {
  id: string;
  message: string;
  severity: string;
  category: string;
  status: string;
  detected_at: string;
  assigned_to?: string;
  resolved_at?: string;
  resolution_notes?: string;
  resource_id?: string;
  resource_data?: Record<string, unknown>;
}

interface ResourceData {
  resource_type: string;
  resource_id: string;
  arn?: string;
  region: string;
  last_seen_at: string;
}

const ViolationDetailsPage: React.FC = () => {
  const { violationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [violation, setViolation] = useState<ViolationData | null>(null);
  const [resource, setResource] = useState<ResourceData | null>(null);
  const [showResolveModal, setShowResolveModal] = useState<boolean>(false);
  const [resolveNotes, setResolveNotes] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (violationId) {
      loadViolation();
    }
  }, [violationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadViolation = async () => {
    setLoading(true);
    setError(null);

    try {
      const violationData = await getGuardrailsViolation(violationId!);
      setViolation(violationData);

      // Load resource details if available
      if (violationData.resource_id) {
        try {
          const resourceData = await getGuardrailsResource(violationData.resource_id);
          setResource(resourceData);
        } catch (err: unknown) {
          console.error('Error loading resource:', err);
        }
      }
    } catch (err: unknown) {
      console.error('Error loading violation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load violation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setProcessing(true);
    try {
      await acknowledgeViolation(violationId!);
      loadViolation();
    } catch (err: unknown) {
      console.error('Error acknowledging violation:', err);
      alert('Failed to acknowledge violation: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleResolve = async () => {
    setProcessing(true);
    try {
      await resolveViolation(violationId!, resolveNotes);
      setShowResolveModal(false);
      setResolveNotes('');
      loadViolation();
    } catch (err: unknown) {
      console.error('Error resolving violation:', err);
      alert('Failed to resolve violation: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const getSeverityColor = (severity: string | undefined): string => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusColor = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'acknowledged':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'false_positive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !violation) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error || 'Violation not found'}</p>
          <Button
            onClick={() => navigate('/cloudops/guardrails/violations')}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Back to Violations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/cloudops/guardrails/violations')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Violations
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getSeverityColor(violation.severity)}`}>
                {violation.severity?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(violation.status)}`}>
                {violation.status?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {violation.message || 'Violation'}
            </h1>
          </div>
          <div className="flex gap-2">
            {violation.status === 'open' && (
              <>
                <Button
                  variant="secondary"
                  onClick={handleAcknowledge}
                  disabled={processing}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Acknowledge
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowResolveModal(true)}
                  disabled={processing}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Resolve
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Violation Information */}
        <Card>
          <CardHeader>
            <CardTitle>Violation Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Message</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{violation.message}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Severity</dt>
                <dd className="mt-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(violation.severity)}`}>
                    {violation.severity?.toUpperCase()}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Category</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{violation.category}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(violation.status)}`}>
                    {violation.status?.toUpperCase()}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Detected At</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(violation.detected_at)}</dd>
              </div>
              {violation.assigned_to && (
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned To</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{violation.assigned_to}</dd>
                </div>
              )}
              {violation.resolved_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved At</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(violation.resolved_at)}</dd>
                </div>
              )}
              {violation.resolution_notes && (
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolution Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{violation.resolution_notes}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Resource Information */}
        {resource && (
          <Card>
            <CardHeader>
              <CardTitle>Resource Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Resource Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{resource.resource_type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Resource ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white break-all">{resource.resource_id}</dd>
                </div>
                {resource.arn && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">ARN</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white break-all">{resource.arn}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Region</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{resource.region}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Seen</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(resource.last_seen_at)}</dd>
                </div>
                {violation.resource_data && Object.keys(violation.resource_data).length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Resource Data</dt>
                    <dd className="mt-1">
                      <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-xs overflow-auto text-gray-900 dark:text-gray-200">
                        {JSON.stringify(violation.resource_data, null, 2)}
                      </pre>
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        title="Resolve Violation"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="resolution-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Resolution Notes
            </label>
            <textarea id="resolution-notes"
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              placeholder="Add notes about how this violation was resolved..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white transition-colors"
              disabled={processing}
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Optional notes explaining how the violation was resolved</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowResolveModal(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleResolve}
              loading={processing}
            >
              Resolve
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default ViolationDetailsPage;

